import { ImageName } from "@mcleod/images";
import { Customer } from "@mcleod/ar";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { getAgentAccessBoolean } from "@mcleod/dispatch";
import {
    Button, ClickEvent, Component, ComponentSearcher, ContextMenuLabelProps, DataDisplayEvent, HorizontalSpacer, Image,
    KeyEvent, Label, LabelProps, Layout, LayoutProps, OpenType, Overlay, Panel, SlideoutDecorator, Snackbar, Table,
    TableColumn, TableRow, TableRowExpansionEvent, Textbox, Toast
} from "@mcleod/components";
import { Alignment, Api, Currency, CurrencySettings, CurrencyUtil, DatePart, DateUtil, DisplayType, DisplayValue, HorizontalAlignment, LicenseTester, LogManager, Model, ModelRow, Navigation, StringUtil } from "@mcleod/core";
import { CommodityQuickInfo, FilterPanel, ModelMovement, ModelOrders, MultiStop, OrderTabView, PostToLoadBoards, ShipmentStatusSlideout } from "@mcleod/dispatch";
import { ManifestMovementQuickInfo } from "@mcleod/dispatch/src/ManifestMovementQuickInfo";
import { PlanningUtil } from "@mcleod/dispatch/src/planning/PlanningUtil";
import { ExchangeRateQuickInfo, getDetentionControlBoolean, LMEGeneralSettings } from "@mcleod/general";
import { BrokerageMovementTableType } from "./BrokerageMovementTableType";
import { BrokerageMovementsGridExpansion } from "./BrokerageMovementsGridExpansion";
import { BrokeragePlanning } from "./BrokeragePlanning";
import { BrokeragePlanningToolPanel } from "./BrokeragePlanningToolPanel";
import { CarrierRatings } from "./CarrierRatings";
import { SmsUtility } from "./SmsUtility";
import { ModelBrokerageStatus } from "./models/ModelBrokerageStatus";
import { RowBrokeragePlanningProfileFields } from "./models/autogen/AutogenModelBrokeragePlanningProfile";
import { BrTrackingQuickInfoPanel } from "./tracking/BrTrackingQuickInfoPanel";
import { DetentionUtil } from "@mcleod/dispatch/src/DetentionUtil";

const SHIPMENT_STATUS = "S";

// Movement status code constants (mirrors server-side RowMovement codes)
const DELIVERED_STATUS_CODE = "D"; // delivered

interface IncludeExcludeList {
    isExclude: boolean;
    dataField: string;
    values: string[];
}

const log = LogManager.getLogger("lme.powerbroker.BrokeragePlanningUtil");

export class BrokeragePlanningUtil {
    static getContextMenuItems(toolPanel: BrokeragePlanningToolPanel, tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        const selectedRows = tableRow?.table?.selectedRows;
        const orderId = selectedRows?.length > 1 && selectedRows?.includes(tableRow)
            ? null
            : tableRow?.data?.get("order_id");
        const result = OrderTabView.addNavigationContextMenuItems(toolPanel.getContextMenuItems(tableRow), orderId);
        this.addOpenSameTabContextMenuItem(result, tableRow);
        return result;
    }

    private static addOpenSameTabContextMenuItem(labelProps: Partial<ContextMenuLabelProps>[], tableRow: TableRow) {
        if (labelProps.some(props => props.text?.startsWith("Open in"))) {
            const openContextMenuLabelProps: Partial<ContextMenuLabelProps> = {
                text: "Open",
                onClick: () => tableRow.table.crudManager.showDetail(OpenType.SAME_TAB)
            };
            labelProps.splice(labelProps.length - 2, 0, openContextMenuLabelProps);
        }
    }
}

export class BrokeragePlanningFilter {
    private filterRow: ModelRow;
    private includeLists: IncludeExcludeList[] = [];
    private orderStartDate: Date;
    private orderEndDate: Date;

    constructor(filterRow: ModelRow) {
        this.filterRow = filterRow;
        this.addIncludeExcludeList("revenue_code");
        this.addIncludeExcludeList("order_type");
        this.addIncludeExcludeList("commodity", "commodity_id");
        this.addIncludeExcludeList("order_customers", "customer_id");
        this.addIncludeExcludeList("order_trlr_type", "equipment_type");
        this.addIncludeExcludeList("operations_user");
        this.addIncludeExcludeList("brokerage_status");
        this.addIncludeExcludeList("dispatcher_user_id", "dispatcher");
        this.addIncludeExcludeList("order_salesperson", "salesperson");
        if (!filterRow.isNull("start_day"))
            this.orderStartDate = DateUtil.parseDateWithKeywords(filterRow.get("start_day"), true, false);
        if (!filterRow.isNull("order_days")) {
            this.orderEndDate = DateUtil.dateAdd(DatePart.DAY, this.orderStartDate ?? new Date(), parseInt(filterRow.get("order_days")));
        }
        log.debug("Created filter", this);
    }

    public dataPasses(data: ModelRow): boolean {
        log.debug("Checking data", data);
        if (!data.getBoolean("brokerage", false) || data.getString("move_status") === "V") {
            return false;
        }

        if (LicenseTester.testPackage("com.tms.client.loadmaster.responsibility") && getAgentAccessBoolean("order_filter")) {
            if (!this.responsibilityDataPasses(data, this.filterRow)) {
                return false;
            }
        }

        for (const list of this.includeLists) {
            const listMatches = this.findItem(list.values, data.get(list.dataField));
            if ((list.isExclude && listMatches) || (!list.isExclude && !listMatches))
                return false;
        }
        if (!this.filterRow.getBoolean("preass_order", false) && !data.isNull("override_payee_id"))
            return false;
        if (!this.filterRow.getBoolean("show_ltl_orders", false) && data.getBoolean("ltl"))
            return false;
        if (this.filterRow.getBoolean("show_ltl_orders", false) && data.getBoolean("ltl")
            && !this.filterRow.getBoolean("include_on_hold_manifests") && data.getBoolean("manifest_hold"))
            return false;
        if (!this.movementTypeMatches(data))
            return false;
        if (!this.inboundOutboundMatches(data))
            return false;
        if (!this.dateTimeMatches(data))
            return false;
        if (!this.apptStatusMatches(data))
            return false;
        if (this.deliveredMoveTooEarly(data) && !this.shouldDisplayUntilReadyToBill(data))
            return false;
        return true;
    }

    private responsibilityDataPasses(data: ModelRow, filterRow: ModelRow): boolean {
        const orderResponsibilityCodes = data.get("responsibility");
        const filterRowAgencies = filterRow.get("order_resp_codes");

        // No filter specified - allow all data
        if (filterRowAgencies == null) {
            return true;
        }

        // Filter specified but order has no responsibility codes - reject
        if (orderResponsibilityCodes == null || orderResponsibilityCodes.length === 0) {
            return false;
        }

        // Check if any order responsibility code matches the filter
        return orderResponsibilityCodes.includes(filterRowAgencies);
    }

    private shouldDisplayUntilReadyToBill(data: ModelRow): boolean {
        return this.filterRow.getBoolean("display_until_ready_to_bill", false)
            && !data.getBoolean("ready_to_bill", false)
            && data.get("move_status") === DELIVERED_STATUS_CODE;
    }

    private deliveredMoveTooEarly(data: ModelRow): boolean {
        if (data.get("move_status") !== DELIVERED_STATUS_CODE)
            return false;

        const destActual = data.get("destination_actual_arrival");
        if (!destActual)
            return false;

        const deliveredMoveDaysBack = 1; // default like server
        const now = new Date();
        const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        startDate.setDate(startDate.getDate() - deliveredMoveDaysBack);
        let destActualDate: Date = typeof destActual === "string" ? DateUtil.parseDate(destActual) : destActual;
        if (!(destActualDate instanceof Date))
            return false;

        destActualDate = DateUtil.justDate(destActualDate);
        const startDateJust = DateUtil.justDate(startDate);
        if (destActualDate.getTime() < startDateJust.getTime())
            return true;
        return false;
    }

    apptStatusMatches(data: ModelRow<any>): boolean {
        const apptStatusFilters = this.tokenizeField("appt_status", false, ",");
        if (apptStatusFilters.length === 0)
            return true;
        const apptStatusData = data.get("stop_appt_statuses", null);
        let matches = apptStatusData?.some(status => this.findItem(apptStatusFilters, status)) ?? false;
        if (this.filterRow.getBoolean("appt_status_exclude", false)) {
            matches = !matches;
        }
        return matches;
    }

    private movementTypeMatches(data: ModelRow): boolean {
        const moveTypeList = this.tokenizeField("movement_type", false, ",");
        switch (data.get("movement_type")) {
            case "TKLD":
                if (!this.findItem(moveTypeList, "T"))
                    return false;
                break;
            case "PDDL":
                if (!this.findItem(moveTypeList, "P"))
                    return false;
                break;
            case "LINE":
                if (!this.findItem(moveTypeList, "L"))
                    return false;
                break;
        }
        return true;
    }

    private inboundOutboundMatches(data: ModelRow): boolean {
        const orderIBList = this.tokenizeField("order_ib_zones", "Z" === this.filterRow.get("order_ib_type"));
        const orderOBList = this.tokenizeField("order_ob_zones", "Z" === this.filterRow.get("order_ob_type"));
        let inbound = this.findItem(orderIBList, this.getFilterValue(data, "ib", "destination"));
        let outbound = this.findItem(orderOBList, this.getFilterValue(data, "ob", "origin"));
        if (this.filterRow.getBoolean("order_ib_exclude", false))
            inbound = !inbound;
        if (this.filterRow.getBoolean("order_ob_exclude", false))
            outbound = !outbound;
        return inbound && outbound;
    }

    private dateTimeMatches(data: ModelRow) {
        let filterDate: Date;
        if (!this.filterRow.isNull("order_ib_zones") && !("*" === this.filterRow.get("order_ib_zones")) &&
            (this.filterRow.isNull("order_ob_zones") || "*" === this.filterRow.get("order_ob_zones")))
            filterDate = data.get("destination_early");
        else
            filterDate = data.get("origin_early");
        if (filterDate == null)
            return false;
        if (typeof filterDate === "string") // bummer - was hoping this would have already been a date in the ModelRow
            filterDate = DateUtil.parseDate(filterDate);
        filterDate = DateUtil.justDate(filterDate);
        return ((this.orderStartDate === null || this.orderStartDate === undefined || this.orderStartDate <= filterDate)
            && (this.orderEndDate === null || this.orderEndDate === undefined || this.orderEndDate >= filterDate));
    }

    private getFilterValue(data: ModelRow, inboundOutboundPart: "ib" | "ob", origDestPart: "origin" | "destination"): string {
        const type = this.filterRow.get(`order_${inboundOutboundPart}_type`);
        switch (type) {
            case "Z":
                return data.get(origDestPart + "_zone");
            case "S":
                return data.get(origDestPart + "_state");
            case "C":
                return data.get(origDestPart + "zip");
            case "L":
                return data.get(origDestPart + "_location_id"); // location_id isn't currently in the data
        }
        return null;
    }

    protected findItem(itemList: string[], item: string): boolean {
        if (itemList == null || itemList.length === 0 || "*" === itemList[0])
            return true;
        if (StringUtil.isEmptyString(item))
            return false;
        return itemList.indexOf(item) >= 0;
    }

    private addIncludeExcludeList(includeExcludeListFieldName: keyof RowBrokeragePlanningProfileFields, dataFieldName?: string, includeExcludeFlagFieldName?: string) {
        this.includeLists.push({
            isExclude: this.filterRow.getBoolean(includeExcludeFlagFieldName || includeExcludeListFieldName + "_exclude", false),
            values: this.tokenizeField(includeExcludeListFieldName, false, ","),
            dataField: dataFieldName || includeExcludeListFieldName
        });
    }

    private tokenizeField(field: keyof RowBrokeragePlanningProfileFields, useZoneParsing: boolean, delimiter: string = ","): string[] {
        if (this.filterRow.isNull(field))
            return [];
        const result = this.filterRow.get(field).split(delimiter);
        for (let i = 0; i < result.length; i++)
            result[i] = result[i].trim();
        return result;
    }

}


export function setTooltipContent(comp: Component, modelRow: ModelRow) {
    if (comp.id == null) return;
    switch (comp.id) {
        case "panelNextStopLoc":
            return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("next_stop_id"), "lme/dispatch/planning/PlanningLocationQuickInfo", {
                minWidth: 128,
                minHeight: 140
            });
        case "panelDestLoc":
            return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("destination_stop_id"), "lme/dispatch/planning/PlanningLocationQuickInfo", {
                minWidth: 128,
                minHeight: 140
            });
        case "panelOriginLoc":
            return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("origin_stop_id"), "lme/dispatch/planning/PlanningLocationQuickInfo", {
                minWidth: 128,
                minHeight: 140
            });
        case "labelTotalCharge":
            return CommonTooltips.setTooltipContentTextboxes(comp, () => [
                {
                    caption: "Freight Charge",
                    displayType: DisplayType.CURRENCY,
                    text: CurrencyUtil.formatCurrency(modelRow.get("freight_charge"))
                },
                {
                    caption: "Other Charges",
                    displayType: DisplayType.CURRENCY,
                    text: CurrencyUtil.formatCurrency(modelRow.get("other_charge_total"))
                }]
            );
        case "textboxTotalPay":
            return CommonTooltips.setTooltipContentTextboxes(comp, () => [
                {
                    caption: "Carrier Pay",
                    displayType: DisplayType.CURRENCY,
                    text: CurrencyUtil.formatCurrency(modelRow.get("override_pay"))
                },
                {
                    caption: "Pay Adjustments",
                    displayType: DisplayType.CURRENCY,
                    text: CurrencyUtil.formatCurrency(modelRow.get("other_pay"))
                }]
            );
        case "textboxMargin":
            return CommonTooltips.setTooltipContentTextboxes(comp, () => [{
                caption: "Margin Percent",
                text: modelRow.get("margin_percent")
            }]);
        case "textboxLastCallinEta":
        case "textboxLastCallinDate":
            return CommonTooltips.setTooltipContentTextboxes(comp, () => [
                {
                    caption: "Last Callin Date",
                    text: modelRow.get("last_callin_date"),
                    displayType: DisplayType.DATETIME
                },
                { caption: "Last Callin City/State", text: modelRow.get("last_callin_loc") },
                { caption: "Last Callin Comment", text: modelRow.get("last_callin_comment") },
                { caption: "Last Callin User", text: modelRow.get("last_callin_user") },
                { caption: "Next Callin Date", text: modelRow.get("next_sched_call") },
                { caption: "Distance to Next Stop", text: modelRow.get("next_stop_miles") }
            ]);
        case "labelPlanningComment":
            comp.tooltipDynamicContent = () => modelRow.get("planning_comment");
            return;
        case "textboxPlanningComment":
            comp.tooltipDynamicContent = () => modelRow.get("planning_comment");
            return;
        case "labelCarrierName":
            return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("carrier_id"), "lme/powerbroker/CarrierQuickInfo", {
                minWidth: 128,
                minHeight: 140
            });
        case "labelLastCallinComment":
            comp.tooltipDynamicContent = () => modelRow.get("last_callin_comment");
            return;
        case "labelTrailer":
            return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("equipment_type"), "lme/dispatch/TrailerTypeQuickInfo", {
                minWidth: 128,
                minHeight: 140
            });
        case "labelCommodity":
            setCommodityTooltipContent(comp, modelRow, {
                minWidth: 128,
                minHeight: 140
            });
            return;
        case "textboxCommodity":
            setCommodityTooltipContent(comp, modelRow, {
                minWidth: 128,
                minHeight: 140
            });
            return;
        case "labelCustomerName":
            return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("customer_id"), "lme/ar/CustomerQuickInfo", {
                minWidth: 128,
                minHeight: 140
            });
        case "panelPickupCityState":
            return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("origin_location_id"), "lme/dispatch/LocationQuickInfo", {
                minWidth: 128,
                minHeight: 140
            });
        case "panelDelCityState":
            return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("destination_location_id"), "lme/dispatch/LocationQuickInfo", {
                minWidth: 128,
                minHeight: 140
            });
        case "textboxCalcMaxPay":
            return makeTargetMaxPayTooltip(comp, modelRow.getCurrency("calc_max_pay"), modelRow.getCurrency("max_per_mile"), modelRow.getNumber("move_distance"));
        case "textboxTargetPay":
            return makeTargetMaxPayTooltip(comp, modelRow.getCurrency("calc_target_pay"), modelRow.getCurrency("target_per_mile"), modelRow.getNumber("move_distance"));
        case "labelConsRefNbr":
            comp.tooltipDynamicContent = () => modelRow.get("cons_ref_nbr");
            return;
        case "labelBOL":
            comp.tooltipDynamicContent = () => modelRow.get("bol");
            return;
        case "labelOriginApptStatus": {
            return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("origin_appt_status"), "lme/dispatch/AppointmentStatusQuickInfo");
        }
        case "labelDestinationApptStatus": {
            return CommonTooltips.setTooltipContentLayout(comp, modelRow.get("destination_appt_status"), "lme/dispatch/AppointmentStatusQuickInfo");
        }
        case "imageServiceIncident": {
            return CommonTooltips.setTooltipContentLayout(comp, null, "lme/dispatch/ServiceIncidentQuickInfo", { maxWidth: 800, maxHeight: 600 }, { movement_id: modelRow.get("movement_id"), order_id: modelRow.get("order_id"), "status": "A" });
        }

    }
}

function makeTargetMaxPayTooltip(textbox: Component, mainCurrency: Currency, perMileCurrency: Currency, perMileDistance: number) {
    let tooltipPanel: Panel;
    if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
        const exchangeLayout = Layout.getLayout("lme/general/multicurrency/ExchangeRateQuickInfo") as ExchangeRateQuickInfo;
        exchangeLayout.addLayoutLoadListener(() => {
            const tableExchangeRates: Table = exchangeLayout.tableExchangeRates;
            tableExchangeRates.addColumn(new TableColumn({
                headingDef: { caption: "Per mile", align: HorizontalAlignment.RIGHT },
                cell: { field: "per_mile", fillRow: true, align: HorizontalAlignment.RIGHT, paddingRight: 12 }
            }), false, true);
            const numOfColumns = tableExchangeRates.columns.length;
            tableExchangeRates.moveColumn(numOfColumns - 1, 1);
            tableExchangeRates.columns[numOfColumns - 1].headingCell.paddingRight = 0;
            tableExchangeRates.width = 500;
            exchangeLayout.sourceCurrencyConversions?.search({
                currency_for_exchange: mainCurrency,
                move_distance: perMileDistance
            });
        });
        tooltipPanel = new Panel({
            components: [new Panel({
                width: 150, rowBreak: false, components: [
                    new Textbox({
                        caption: "Per Mile",
                        displayType: DisplayType.CURRENCY,
                        text: perMileCurrency?.toString(),
                        printable: true,
                        fontBold: true
                    })
                ]
            }),
                exchangeLayout]
        });
        textbox.tooltipDynamicContent = tooltipPanel;
    } else {
        CommonTooltips.setTooltipContentTextboxes(textbox, () => [{
            caption: "Per Mile",
            displayType: DisplayType.CURRENCY,
            text: perMileCurrency.toString()
        }]);
    }
}

export function setCommodityTooltipContent(component: Component, data: ModelRow, layoutProps?: Partial<LayoutProps>) {
    component.tooltipDynamicContent = async (): Promise<Layout> => {
        return await getLoadedCommodityQuickInfoLayout(data, layoutProps);
    };
}

export function overrideCommodityQuickInfoTooltip(component: Textbox|Label, data: ModelRow, layoutProps?: Partial<LayoutProps>) {
    component.quickInfoOverrideMethod = async (): Promise<Layout> => {
        return await getLoadedCommodityQuickInfoLayout(data, layoutProps);
    };
}

async function getLoadedCommodityQuickInfoLayout(data: ModelRow, layoutProps?: Partial<LayoutProps>) {
    const commodityId = data?.get("commodity_id");
    if (StringUtil.isEmptyString(commodityId)) {
        return null;
    }
    const layout = await Layout.getLoadedLayout("lme/dispatch/CommodityQuickInfo", layoutProps) as CommodityQuickInfo;
    await layout.mainDataSource.search({ id: commodityId });
    if (!data.isNull("order_id")) {
        await layout.sourceEquipMatchDetail.search({
            _parent_link: {
                model: "lme/dispatch/movement",
                "orders.id": data.get("order_id")
            }
        });
        layout.panelLoadRequirements.visible = layout.sourceEquipMatchDetail.data.length > 0;
    }
    return layout;
}

export function setRowComponentVisible(tableRow: TableRow, componentOrId: string | Component, fieldName: string, valueForVisible?: any) {
    if (fieldName != null) {
        const component = (typeof componentOrId === "string") ? tableRow?.findComponentById(componentOrId) : componentOrId;
        const fieldValue = tableRow.data?.get(fieldName);
        if (component != null) {
            component.visible = valueForVisible != null ? valueForVisible === fieldValue : fieldValue != null;
        }
    }
}

export function updateBrokerageStatus(brokerageStatusPanel: Panel, movementId: string, statusRow: ModelRow, brkMovementTable: Table, dispatchButton: Button) {
    const statusCode = statusRow.get("id");
    const tableRow = TableRow.getContainingTableRow(brokerageStatusPanel);
    brkMovementTable.selectedRow = tableRow;
    if (statusRow.getBoolean("display_callin_screen")) {
        postBrokerageStatusWithCallin(tableRow, statusRow, brkMovementTable, dispatchButton);
    } else {
        postBrokerageStatusUpdate(movementId, statusCode, brokerageStatusPanel);
    }

    const triggerCode = LMEGeneralSettings.getSingleton().getDispatchControl().rating_trigger_code;
    if (!StringUtil.isEmptyString(triggerCode) && triggerCode === statusCode) {
        CarrierRatings.show({
            carrierRatingHeaderData: {
                order_id: tableRow.data.get("order_id"),
                movement_id: movementId,
                payee_id: tableRow.data.get("carrier_id")
            },
            carrier_name: tableRow.data.get("carrier_name")
        });
    }
}

export function postBrokerageStatusWithCallin(tableRow: TableRow, statusRow: ModelRow, brkMovementTable: Table, buttonDispatch: Button) {
    if (buttonDispatch == null)
        return;
    const data = tableRow.data;
    data.setValues({
        brokerage_status_descr: statusRow.get("descr"),
        brokerage_status: statusRow.get("id"),
        brokerage_status_color: statusRow.get("planning_hex_color")
    });
    setupBrkStatusPanel(tableRow);
    brkMovementTable["callinRequiredBrkStatusCode"] = statusRow;
    buttonDispatch.clicked();
}

export function setupBrkStatusPanel(tableRow: TableRow) {
    const labelBrkStatus = tableRow?.findComponentById("labelBrkStatus") as Label;
    const brkStatusDesc = tableRow?.data?.get("brokerage_status_descr");
    const brkStatus = tableRow?.data?.get("brokerage_status");
    const panel = tableRow?.findComponentById("panelBrkStatus") as Panel;
    const imageBrokerageStatus = tableRow.findComponentById("imageBrokerageStatus") as Image;
    if (imageBrokerageStatus?.busy === true)
        imageBrokerageStatus.busy = false;
    if (panel != null) {
        const bgColor = tableRow.data.get("brokerage_status_color");
        if (bgColor === "#ffffff") {
            panel.borderWidth = 1;
            panel.borderColor = "primary";
            panel.backgroundColor = bgColor;
            if (labelBrkStatus) {
                labelBrkStatus.color = "primary";
            }
            if (imageBrokerageStatus) {
                imageBrokerageStatus.color = "primary";
            }
        } else {
            panel.borderWidth = 0;
            panel.backgroundColor = bgColor;
            if (labelBrkStatus) {
                labelBrkStatus.color = "default.reverse";
            }
            if (imageBrokerageStatus) {
                imageBrokerageStatus.color = "default.reverse";
            }
        }
        if (labelBrkStatus) {
            labelBrkStatus.text = brkStatus || "--";
        }

        if (brkStatusDesc != null) {
            panel.tooltipDynamicContent = brkStatusDesc;
        }
        else {
            if (labelBrkStatus) {
                labelBrkStatus.color = "default";
            }
            panel.borderWidth = 1;
            panel.borderColor = "subtle";
            panel.tooltip = "Click the drop down to set the brokerage status code";
            if (imageBrokerageStatus) {
                imageBrokerageStatus.color = "subtle";
            }
        }
    }
}

export function tableSearcherCreationCallback(): ComponentSearcher[] {
    return [
        new ComponentSearcher("brokerage_status"),
        new ComponentSearcher("brokerage_status_descr")
    ];
}

export function postBrokerageStatusUpdate(movementId: string, statusCode: string, brokerageStatusPanel: Panel) {
    const brkStatusImg = brokerageStatusPanel.findComponentById("imageBrokerageStatus") as Image;
    if (brkStatusImg != null)
        brkStatusImg.busy = true;
    new ModelMovement().searchSingle({ id: movementId }, "lme/dispatch/Movement").then((movementRow) => {
        movementRow.set("brokerage_status", statusCode);
        movementRow.post().then(row => {
            log.debug("movement posted");
        }).catch(reason => {
            CommonDialogs.showError(reason);
        }).finally(() => {
            if (brkStatusImg != null)
                brkStatusImg.busy = false;
        });
    });
}

export function populateAndDisplayBrkStatusDropDowns(panel: Panel, movementId: string, brkMovementTable: Table, dispatchButton: Button) {
    const dropDownItems: Partial<LabelProps>[] = [];
    new ModelBrokerageStatus().search({ movement_id: movementId }).then(result => {
        if (result?.modelRows?.length > 0) {
            result.modelRows.forEach(row => {
                const labelProps: Partial<LabelProps> = {
                    text: row.get("descr", "id"),
                    onClick: () => updateBrokerageStatus(panel, movementId, row, brkMovementTable, dispatchButton)
                }
                dropDownItems.push(labelProps);
            });
        }
    }).then(() => {
        panel["statusDropDownItems"] = dropDownItems;
        displayBrkStatusDropDowns(panel);
    });
}

export function displayBrkStatusDropDowns(panel: Panel) {
    const dropDownItems = panel["statusDropDownItems"];
    if (dropDownItems != null) {
        Overlay.showDropdown(panel, dropDownItems, null, { width: 225 }, {
            align: Alignment.LEFT,
            onClose: () => {
                panel.borderShadow = false;
            }
        });
    }
}

// START - Minute updater stuff
/**
 * Returns the component that the late functionality will be applied to.
 * @param tableRow The TableRow the component is on.
 * @param stop Determines if this is for the origin or destination.
 * @param type The table type.
 * @returns
 */
export function getLateDateComp(tableRow: TableRow, stop: "origin" | "destination") {
    return tableRow.findComponentById(stop === "origin" ? "imageOriginLate" : "imageDestLate") as Image;
}

/**
 * This function gets called on every tab/bucket with every minute change.  Updates to the TableRow should be called from updateTableRow(...).
 * @param table
 */
export async function tableMinuteUpdate(table: BrokerageMovementTable) {
    table?.tableMovements.rows.forEach(row => rowMinuteUpdate(row, table.type))
}

/**
 * This function will get called when the time's minute changes.  It also gets called from the TableRow's onRowDisplay event.
 * Call functions that need to happen with the time's minute change here.
 * @param tableRow The TableRow that will be effected.
 * @param type The table type / bucket.
 */
export function rowMinuteUpdate(tableRow: TableRow, type: BrokerageMovementTableType) {
    if (!tableRow.populatedDOM)
        return
    checkScheduledLate(tableRow, "origin", type);
    checkScheduledLate(tableRow, "destination", type);
    updatePendingBadge(tableRow, type);
}

/**
 * Updates the badge the listener is tied to.
 * @param tableRow
 * @param stop
 * @param type
 */
function checkScheduledLate(tableRow: TableRow, stop: "origin" | "destination", type: BrokerageMovementTableType) {
    const compAppointmentLate = getLateDateComp(tableRow, stop);

    if (compAppointmentLate == null || [BrokerageMovementTableType.DELIVERED].includes(type))
        return;

    const isOrigin = "origin" === stop;

    const apptTimeDiff = getApptTimeDiff(tableRow, stop);
    const isApptLate = apptTimeDiff < 0;
    const isApptApproaching = apptTimeDiff != null;
    compAppointmentLate.tooltip = isApptLate ? `This movement's ${isOrigin ? "pickup" : "delivery"} time has passed` : isApptApproaching ? getLateImageTooltip(apptTimeDiff, isOrigin ? "Pickup" : "Deliver") : null;

    compAppointmentLate.visible = compAppointmentLate.tooltip != null;
    compAppointmentLate.backgroundColor = isApptLate ? "error" : isApptApproaching ? "warning" : "";
    if (BrokerageMovementTableType.PERISCOPE === type) {
        const panelTimeComp = tableRow.findComponentById(isOrigin ? "panelOriginTime" : "panelDestTime") as Panel;
        if (panelTimeComp != null) {
            panelTimeComp.tooltip = compAppointmentLate.tooltip;
        }
    } else {
        const fullTimeComp = tableRow.findComponentById(isOrigin ? "labelOriginFullTime" : "labelDestFullTime") as Textbox;
        if (fullTimeComp != null) {
            fullTimeComp.tooltip = compAppointmentLate.tooltip;
        }
    }

    function getApptTimeDiff(tableRow: TableRow, stop: "origin" | "destination"): number {
        const dispatchControl = LMEGeneralSettings.getSingleton().getDispatchControl();
        const isOrigin = "origin" === stop;
        const warning = isOrigin ? dispatchControl.pickup_warning : dispatchControl.delivery_warning;
        const diff = getTimeDiff(new Date(PlanningUtil.getStopTime(tableRow?.data, stop)));

        if (diff == null || (diff > (warning * 60 * 60 * 1000)) || (tableRow.data.get(`${stop}_actual_arrival`) != null)) return null;
        return diff;
    }
}

export function getLateImageTooltip(millis: number, stopVerb: "Pickup" | "Next Stop" | "Deliver"): string {
    const hh = Math.floor(millis / 1000 / 60 / 60);
    millis -= hh * 1000 * 60 * 60;
    const mm = Math.ceil(millis / 1000 / 60);
    let result = `${stopVerb} in`;

    // Set the hours
    if (mm == 60) result += ` ${hh + 1}hr`;
    else if (hh > 0) {
        result += ` ${hh}hr`
    }

    // Set the minutes
    if (mm < 60 && mm > 0) {
        result += " ";
        if (hh > 0 && mm.toString().length == 1) result += "0";
        result += `${mm}m`;
    }
    return result;
}

export function getTrackingLateTooltip(millis: number): string {
    // build string in format "Tracking X hrs Y mins Late"
    const hh = Math.floor(millis / 1000 / 60 / 60);
    millis -= hh * 1000 * 60 * 60;
    const mm = Math.ceil(millis / 1000 / 60);
    let result = "Tracking";

    // Set the hours
    if (mm == 60) result += ` ${hh + 1}hr`;
    else if (hh > 0) {
        result += ` ${hh}hr`
    }

    // Set the minutes
    if (mm < 60 && mm > 0) {
        result += ` ${mm}m`;
    }

    result += " Late";
    return result;
}

function updatePendingBadge(tableRow: TableRow, type: BrokerageMovementTableType) {
    const badge = tableRow.findComponentById("labelPendingBadge") as Label;
    const unlockTime = tableRow.data.get("unlock_time");

    if (BrokerageMovementTableType.AVAILABLE !== type || badge == null || unlockTime == null)
        return;
    const minutesUntilLate = Math.ceil(getTimeDiff(new Date(unlockTime)) / (1000 * 60) % 60);
    if (minutesUntilLate <= 5) {
        badge.backgroundColor = "error";
    }
}

/**
 * Returns the difference in time from the given time to the current time (relative to user's timezone).
 * Returned number is in milliseconds.
 * If the returned value is negative, it means the given time has passed.
 * @param date The time to compare against.
 * @returns
 */
export function getTimeDiff(date: Date): number {
    return date.getTime() - new Date().getTime();
}

export interface BrokerageMovementTable extends Layout {
    type: BrokerageMovementTableType;
    brokeragePlanning: BrokeragePlanning;
    availFilterPanel: FilterPanel;
    tableMovements: Table;
    panelSecondaryFilters: Panel;
    layoutBrokeragePlanningToolPanel: BrokeragePlanningToolPanel;
    configureTableForType: (type: BrokerageMovementTableType) => void;
}

export function instanceOfBrokerageMoveTable(object: any): object is BrokerageMovementTable {
    return object?.tableMovements && object?.panelSecondaryFilters && object?.layoutBrokeragePlanningToolPanel;
}

export class BrokerageMovementTableConfigure {

    static async brokerageMovementTableOnLoad(tableMovements: Table, layoutBrokeragePlanningToolPanel: BrokeragePlanningToolPanel) {
        layoutBrokeragePlanningToolPanel = Layout.getLayout("lme/powerbroker/BrokeragePlanningToolPanel") as BrokeragePlanningToolPanel;
        await layoutBrokeragePlanningToolPanel.ensureLoaded();
        layoutBrokeragePlanningToolPanel.table = tableMovements;
        layoutBrokeragePlanningToolPanel.initialize();
    }


    static configureBrokerageTableForType(tableMovements: Table, type: BrokerageMovementTableType, filterPanel: FilterPanel, panelSecondaryFilters: Panel) {
        tableMovements.exportName = "Brokerage Planning Listing (" + type.caption + ") - {timestamp}";
        tableMovements.id += type.tableIdSuffix;
        filterPanel = new FilterPanel(type.secondaryFilters, tableMovements, panelSecondaryFilters);
        tableMovements.dataSource.orderBy = type.defaultSort;
        tableMovements.overrideUrlParams = (selectedRow: TableRow) => {
            return {
                key: selectedRow.data.get("movement_id"),
                orderId: selectedRow.data.get("order_id", selectedRow.data.get("manifest_id"))
            };
        }
    }
}

const tooltipRightAlignProps = { position: Alignment.RIGHT };

export class BrokerageMovementTableListeners {

    static brokerageMovementTableonRowDisplayBPListener(tableRow: TableRow, tableMovements: Table, layoutBrokeragePlanningToolPanel?: BrokeragePlanningToolPanel, type?: BrokerageMovementTableType) {
        const modelRow: ModelRow = tableRow.data;
        const manifestId = modelRow.getString("manifest_id", null);
        const orderIdLabel = tableRow.findComponentById("labelOrderId") as Label;
        if (orderIdLabel != null) {
            orderIdLabel.addUnderlineWhenMouseOver();
            orderIdLabel.visible = manifestId == null;
        }
        const customerLabel = tableRow.findComponentById("labelCustomer") as Label;
        if (customerLabel != null) {
            customerLabel.addUnderlineWhenMouseOver();
        }
        const imageOnHold = tableRow.findComponentById("imageOnHold") as Button;
        if (imageOnHold != null) {
            imageOnHold.visible = modelRow.getBoolean("hold", false) && manifestId == null;
            imageOnHold.tooltip = modelRow.getString("hold_reason");
        }
        const imageManifestOnHold = tableRow.findComponentById("imageManifestOnHold") as Button;
        if (imageManifestOnHold != null) {
            imageManifestOnHold.visible = modelRow.getBoolean("manifest_hold", false);
            imageManifestOnHold.tooltip = "Manifest On Hold";
        }
        if (layoutBrokeragePlanningToolPanel != null) {
            tableRow.contextMenuItems = () => BrokeragePlanningUtil.getContextMenuItems(layoutBrokeragePlanningToolPanel, tableRow);
        }
        setupBrkStatusPanel(tableRow);
        tableMovements.addlSearcherCallback = (): ComponentSearcher[] => tableSearcherCreationCallback();
        if (type) {
            rowMinuteUpdate(tableRow, type);
        }
        PlanningUtil.displayBadges(tableRow);
        this.displayEmailLabel(tableRow);
        this.displaySmsBtn(tableRow);
        this.displayIcons(tableRow);

        const intercompanyImage = tableRow.findComponentById("imageInterCompany") as Image;
        if (intercompanyImage != null) {
            intercompanyImage.visible = modelRow.getBoolean("licensed_for_ic", false) && !modelRow.isNull("ediorder_intercompany_key");
        }

        const imageHazmat = tableRow.findComponentById("imageHazmat") as Image;
        if (imageHazmat != null) {
            imageHazmat.visible = !modelRow.isNull("hazmat_code");
        }

        const panelCommodity = tableRow.findComponentById("panelCommodity") as Panel;
        if (panelCommodity != null && modelRow.get("hazmat_code") != null) {
            CommonTooltips.setTooltipContentTextboxes(panelCommodity, () => [
                { caption: "Hazmat Code", displayType: DisplayType.CURRENCY, text: modelRow.get("hazmat_code") }
            ])
        }
        PlanningUtil.checkAppointmentRequiredConfirmed(tableRow);

        PlanningUtil.checkScheduledLate(tableRow, "origin");
        PlanningUtil.checkScheduledLate(tableRow, "destination");

        const labelStopsPickDrop = tableRow.findComponentById("labelStopsPickDrop") as Label;
        if (labelStopsPickDrop != null) {
            labelStopsPickDrop.applyLinkStyle();
            labelStopsPickDrop.applyButtonHoverStyle();
            CommonTooltips.setTooltipContentLayout(labelStopsPickDrop, null, "lme/dispatch/StopAppointmentStatusList", {
                maxWidth: 800,
                maxHeight: 600
            }, { movement_id: modelRow.get("movement_id") });
        }
        const ysplitPanel = tableRow.findComponentById("panelYsplit") as Panel;
        if (ysplitPanel != null) {
            ysplitPanel.forEveryChildComponent(comp => {
                comp.visible = false;
            });
        }

        const originStopType = modelRow.get("origin_stop_type", "");
        const destStopType = modelRow.get("destination_stop_type", "");
        if (originStopType == "SP" || destStopType == "SD") {
            const imageId = originStopType === "SP" && destStopType === "SD" ? "imageYsplitComplete" : originStopType === "SP" ? "imageYsplitPick" : destStopType === "SD" ? "imageYsplit" : null;
            const imageYsplit = tableRow.findComponentById(imageId) as Image;
            if (imageYsplit != null) {
                imageYsplit.visible = true;
            }
        }

        const originApptStatus = tableRow.findComponentById("labelOriginApptStatus") as Label;
        if (originApptStatus != null) {
            originApptStatus.visible = !modelRow.isNull("origin_appt_status");
            originApptStatus.backgroundColor = modelRow.getString("origin_appt_status_color");
            originApptStatus.text = modelRow.getString("origin_appt_status");
        }
        const destinationApptStatus = tableRow.findComponentById("labelDestinationApptStatus") as Label;
        if (destinationApptStatus != null) {
            destinationApptStatus.visible = !modelRow.isNull("destination_appt_status");
            destinationApptStatus.backgroundColor = modelRow.getString("destination_appt_status_color");
            destinationApptStatus.text = modelRow.getString("destination_appt_status");
        }

        tableRow.forEveryChildComponent(comp => {
            if (comp instanceof Textbox && (comp.displayType === DisplayType.CURRENCY || comp.displayType === DisplayType.DECIMAL) && comp.printableLabel != null)
                comp.printableLabel.align = HorizontalAlignment.LEFT;
            this.setupComponentsOnRowDisplayBPListener(comp, modelRow);
        });
        const manifestIdLabel = tableRow.findComponentById("labelManifestId") as Label;
        if (manifestIdLabel != null) {
            const manifestId = modelRow.get("manifest_id");
            const movementId = modelRow.get("movement_id");
            ManifestMovementQuickInfo.addHoverToDiscoverToManifestIdComponent(manifestIdLabel, movementId, manifestId);
        }

        if (manifestId == null) {
            const componentIds = [ "textboxManifestSalesperson", "textboxManifestCustomers", "textboxManifestOrderTypes", "textboxManifestRevenueCodes", "panelManifestCommodity"];
            this.setVisibilityComponent(tableRow, componentIds );
        } else {
            const componentIds = ["textboxSalesPerson", "labelCustomerName", "textboxOrderType", "textboxRevenueCode", "panelCommodity" ];
            this.setVisibilityComponent(tableRow, componentIds);
            const panelManifestCommodity = tableRow.findComponentById("panelManifestCommodity");
            if (panelManifestCommodity != null) {
                panelManifestCommodity.visible = true;
            }
            const imageManifestHazmatCode = tableRow.findComponentById("imageManifestHazmatCode") as Image;
            if (imageManifestHazmatCode != null) {
                imageManifestHazmatCode.visible = !modelRow.isNull("hazmat_code");
            }

            const manifestFields = [
                { field: "manifest_salesperson", component: "textboxManifestSalesperson" },
                { field: "manifest_order_type", component: "textboxManifestOrderTypes" },
                { field: "manifest_revenue", component: "textboxManifestRevenueCodes" }
            ];

            for (const { field, component } of manifestFields) {
                if (modelRow.isNull(field)) {
                    this.hideComponent(tableRow, component);
                }
            }
        }

        const textboxPlanningComment = tableRow.findComponentById("textboxPlanningComment") as Textbox;
        if (textboxPlanningComment != null) {
            const saveButton = tableRow.findComponentById("buttonSaveComment") as Button;
            textboxPlanningComment.addKeyDownListener((event: KeyEvent) => PlanningUtil.savePlanningCommentOnKeyDown(event, saveButton));
        }
    }

    static setVisibilityComponent(tableRow: TableRow, componentIds: string[]) {
        for (const id of componentIds) {
            this.hideComponent(tableRow, id);
        }
    }


    private static hideComponent(tableRow: TableRow, id: string) {
        const component = tableRow.findComponentById(id);
        if (component) {
            component.visible = false;
        }
    }

    static setupComponentsOnRowDisplayBPListener(comp: Component, modelRow: ModelRow) {
        setTooltipContent(comp, modelRow);
        const isAvailable = "A" === modelRow?.get("move_status");
        switch (comp.id) {
            case "textboxLastCallinEta": {
                const textboxLastCallinEta = comp as Textbox;
                if (textboxLastCallinEta.text != null && modelRow.get("next_stop_timezone") != null) {
                    textboxLastCallinEta.text = DisplayValue.getDisplayValue(modelRow.getDateTime("last_callin_eta"), DisplayType.DATETIME) + " " + modelRow.get("next_stop_timezone");
                }
                break;
            }
            case "labelEtaToNextStop": {
                const etaToNextStop = modelRow.get("last_callin_eta");
                comp.visible = etaToNextStop != null;
                break;
            }
            case "textboxPnnRate":
            case "textboxTargetPay":
            case "textboxCalcMaxPay": {
                comp.visible = isAvailable && modelRow?.get(comp.field) != null;
                break;
            }
            case "textboxTotalPay":
            case "textboxMargin": {
                comp.visible = !isAvailable && modelRow?.get(comp.field) != null;
                break;
            }
            case "labelManifestId": {
                comp.visible = modelRow?.get("manifest_id") != null;
                break;
            }
        }

    }

    static displayIcons(tableRow: TableRow) {
        this.displayRateConImage(tableRow.findComponentById("imageRateCon") as Image, tableRow.data);
        this.displayLockImage(tableRow.findComponentById("imageLock") as Image, tableRow.data);
        this.displayLoadBoardImage(tableRow);
        this.displayDigitalOfferImage(tableRow);
        this.displayServiceFailureImage(tableRow);
        this.displayPackageErrorImage(tableRow);
        this.displayHighwayErrorImage(tableRow);
        this.displayDetentionIcon(tableRow);
    }

    static async displayHighwayErrorImage(tableRow: TableRow) {
        const imageHighwayError = tableRow.findComponentById("imageHighwayError") as Image;
        if (imageHighwayError == null) return;
        if (tableRow.data.getBoolean("has_load_lock_alert")) {
            imageHighwayError.visible = true;
            const highwayAlerts = await this.getHighwayAlerts(tableRow);
            const response = highwayAlerts.data[0];
            const alerts = response["alerts"];
            const panelHighwayErrorTooltip = this.buildPanelLoadLockAlertTooltip(alerts || []);
            imageHighwayError.tooltipDynamicContent = panelHighwayErrorTooltip;
        } else {
            imageHighwayError.visible = false;
        }
    }

    static async getHighwayAlerts(tableRow: TableRow) {
        const orderId = tableRow.data.get("order_id");
        const movementId = tableRow.data.get("movement_id");
        const response = await Api.search("lme/powerbroker/highway-load_lock-alerts", { order_id: orderId, movement_id: movementId });
        return response;
    }

    static buildPanelLoadLockAlertTooltip(alerts: string[]) {
        const panelLoadLockAlertTooltip = new Panel({ id: "panelLoadLockAlertTooltip" });
        panelLoadLockAlertTooltip.add(new Label({ text: "Load Lock Alerts", fontBold: true, marginBottom: 10 }));
        alerts.forEach((alertText) => {
            panelLoadLockAlertTooltip.add(new Label({ text:  `• ` + DateUtil.formatDateTime(new Date(alertText[0]), DateUtil.getUserDateTimeFormat()) + ` - ` +  alertText[1]}));
        });
        return panelLoadLockAlertTooltip;
    }

    static displayServiceFailureImage(tableRow: TableRow) {
        const imageServiceIncident = tableRow.findComponentById("imageServiceIncident") as Image;
        if (imageServiceIncident == null) return;
        if (tableRow.data.getBoolean("has_service_failures")) {
            imageServiceIncident.visible = true;
        } else {
            imageServiceIncident.visible = false;
        }
    }

    static displayEmailLabel(tableRow: TableRow) {
        const labelEmail = tableRow?.findComponentById("labelCarrierDriverEmail") as Label;
        setRowComponentVisible(tableRow, labelEmail, "carrier_driver_email");
        if (labelEmail && labelEmail.visible) {
            const email = tableRow.data.get("carrier_driver_email");
            if (email == null) return;
            labelEmail.link = "mailto:" + email;
            labelEmail.tooltipDynamicContent = {
                text: email,
                displayType: DisplayType.EMAIL
            };
        }
    }

    static async displaySmsBtn(tableRow: TableRow) {
        if (tableRow.data.getBoolean("is_sms_licensed")) {
            const smsBtn = tableRow?.findComponentById("sendSmsBtn") as Button;
            const carrierDriverName = tableRow?.findComponentById("labelCarrierDriverName") as Label;
            const carrierDriverCell = tableRow?.findComponentById("labelCarrierDriverCell") as Label;
            const carrierDriver2Name = tableRow?.findComponentById("labelCarrierDriver2Name") as Label;
            const carrierDriver2Cell = tableRow?.findComponentById("labelCarrierDriver2Cell") as Label;
            const carrierId = tableRow?.data?.get("carrier_id");
            const orderId = tableRow?.data?.get("order_id");
            const driverOneOptedOut = tableRow?.data?.getBoolean("opted_out_driver_one");
            const driverTwoOptedOut = tableRow?.data?.getBoolean("opted_out_driver_two");

            const drivers = [
                { label: carrierDriverName, tooltip: "" },
                { label: carrierDriverCell, tooltip: "" },
                { label: carrierDriver2Name, tooltip: "" },
                { label: carrierDriver2Cell, tooltip: "" }
            ];

            drivers.forEach(driver => {
                if (driver.label && driver.label.text === undefined) {
                    driver.label.tooltip = "";
                    const parentNode = driver.label.parent;
                    parentNode.remove(driver.label);
                }
            });

            if (smsBtn && smsBtn.imageName === undefined) {
                smsBtn.visible = true;
                if (driverOneOptedOut && carrierDriver2Name.text === undefined) {
                    smsBtn.imageName = ImageName.driverSmsOptedOut;
                    smsBtn.tooltip = "Driver Opted Out";
                } else if (driverOneOptedOut && driverTwoOptedOut) {
                    smsBtn.imageName = ImageName.driverSmsOptedOut;
                    smsBtn.tooltip = "Driver Opted Out";
                } else if (tableRow.data.get("inbound_unread_text_messages_exist") > 0) {
                    smsBtn.imageName = ImageName.driverMsgUnread;
                    CommonTooltips.setTooltipContentLayout(smsBtn, null, "lme/powerbroker/SmsQuickInfo", {
                        maxWidth: 400,
                        maxHeight: 400
                    }, { order_id: orderId });
                    smsBtn.tooltipDynamicOptions = tooltipRightAlignProps;
                } else if (tableRow.data.get("text_messages_exist")) {
                    smsBtn.imageName = ImageName.driverMsgUnread;
                    smsBtn.imageColor = "primary";
                    CommonTooltips.setTooltipContentLayout(smsBtn, null, "lme/powerbroker/SmsQuickInfo", {
                        maxWidth: 400,
                        maxHeight: 400
                    }, { order_id: orderId });
                    smsBtn.tooltipDynamicOptions = tooltipRightAlignProps;
                } else {
                    smsBtn.imageName = ImageName.driverMsgNormal;
                    smsBtn.tooltip = "Send Message";
                }
            }
        }
    }


    static displayRateConImage(imageRateCon: Image, modelRow: ModelRow) {
        if (imageRateCon == null) return;
        const rateConStatus = modelRow.get("rate_con_status");
        const imageName = modelRow.get("rate_con_status_image_name");
        imageRateCon.visible = imageName != null;
        if (rateConStatus != null) {
            imageRateCon.name = imageName;
            imageRateCon.color = modelRow.get("rate_con_status_image_color", "default");
            CommonTooltips.setTooltipContentTextboxes(imageRateCon, () => [
                { caption: "Rate Con Status", text: modelRow.get("rate_con_status_desc") }
            ]);
            imageRateCon.tooltipDynamicOptions = tooltipRightAlignProps;
        }
    }

    static displayLockImage(imageRateLock: Image, modelRow: ModelRow) {
        if (imageRateLock == null) return;
        const locked = modelRow.get("locked", false);
        imageRateLock.visible = locked;
        if (locked) {
            const waterfallInProgress = modelRow.get("waterfall_in_progress", false);
            imageRateLock.name = waterfallInProgress ? ImageName.waterfall : ImageName.lock;
            CommonTooltips.setTooltipContentLayout(imageRateLock, modelRow, "lme/powerbroker/MovementLockQuickInfo", {
                minWidth: 100,
                minHeight: 100
            });
            imageRateLock.tooltipDynamicOptions = tooltipRightAlignProps;
        }
    }

    static async displayLoadBoardImage(tableRow: TableRow) {
        const imageLoadboard = this.findImageComponent(tableRow, "imageLoadboard");
        const imageLoadboardError = this.findImageComponent(tableRow, "imagePostingLoadboardError");
        const imagePostedByMovement = this.findImageComponent(tableRow, "imagePostedByMovement");
        const movementStatus = tableRow.data.get("move_status") === "A";

        try {
            if (!movementStatus) {
                return;
            }
            if (this.isPostedByMovement(tableRow)) {
                this.handlePostedByMovement(imagePostedByMovement, imageLoadboardError, imageLoadboard, tableRow);
            } else if (this.isPostedByDifferentMovement(tableRow)) {
                imageLoadboard.visible = false;
            } else if (this.shouldShowLoadboard(tableRow)) {
                this.handleLoadboardImageVisibility(tableRow, imageLoadboard, imageLoadboardError);
            } else {
                this.hideImages(imageLoadboard, imageLoadboardError);
            }
        } catch (error) {
            console.error("API call failed:", error);
        }
    }

    static findImageComponent(tableRow, componentId) {
        return tableRow.findComponentById(componentId) as Image;
    }

    static isPostedByMovement(tableRow) {
        return tableRow.data.get("posted_by_movement") && tableRow.data.get("is_posted_movement");
    }

    static handlePostedByMovement(imagePostedByMovement, imageLoadboardError, imageLoadboard, tableRow) {
        const reasonsList = tableRow?.data?.get("loadboard_exception_list");
        if (reasonsList && Array.isArray(reasonsList) && reasonsList.length > 0) {
            this.handleLoadboardErrorTooltip(tableRow, reasonsList, imageLoadboardError);
            imageLoadboardError.visible = true;
        } else {
            imageLoadboardError.visible = false;
            imageLoadboard.visible = true;
            this.setUpLoadBoardPostingSuccessfulTooltip(tableRow, imageLoadboard, imageLoadboardError);
        }
    }

    static isPostedByDifferentMovement(tableRow) {
        return tableRow.data.get("posted_by_movement") && !tableRow.data.get("is_posted_movement");
    }

    static shouldShowLoadboard(tableRow) {
        //  return tableRow.data.getBoolean("loadboard") && tableRow.data.get("move_status") === "A" && tableRow.data.get("brokerage_status") === "AVAIL" && !StringUtil.isEmptyString(tableRow.data.get("order_id"));
        return tableRow.data.getBoolean("loadboard") && !StringUtil.isEmptyString(tableRow.data.get("order_id"));
    }

    static handleLoadboardImageVisibility(tableRow, imageLoadboard, imageLoadboardError) {
        const reasonsList = tableRow?.data?.get("loadboard_exception_list");
        if (reasonsList && Array.isArray(reasonsList) && reasonsList.length > 0) {
            this.handleLoadboardErrorTooltip(tableRow, reasonsList, imageLoadboardError);
            imageLoadboardError.visible = true;
        } else {
            this.setUpLoadBoardPostingSuccessfulTooltip(tableRow, imageLoadboard, imageLoadboardError);
        }
    }

    static handleLoadboardErrorTooltip(tableRow, reasonsList, imageLoadboardError) {
        const uniqueReasons = [...new Set(reasonsList)];
        const uniqueFormattedReasons = uniqueReasons.map(reason => `• ${reason}`).join("\n");
        if (uniqueFormattedReasons) {
            imageLoadboardError.visible = true;
            this.setUpLoadBoardErrorTooltip(tableRow, uniqueFormattedReasons, imageLoadboardError);
        }
    }

    static async setUpLoadBoardErrorTooltip(tableRow: TableRow, message: string, imageLoadboardError: Image) {
        const result = await Model.searchSingleRecord("lme/powerbroker/pbw-loadboard-services", { service_type: "T" });
        const isDatRestControlActive = StringUtil.equalsIgnoreCase(result?.data?.is_active, "Y");

        imageLoadboardError.tooltipDynamicContent = () => {
            const modelRow = tableRow.data as ModelRow;
            const panel = new Panel({ padding: 0, margin: 0, wrap: true, maxWidth: 500 });
            panel.add(new Label({
                text: "Load Board Error",
                fontBold: true,
                color: "error",
                imageName: ImageName.triangleWarning,
                rowBreak: true
            }));
            panel.add(new Label({
                text: "There is an error posting to the Load Board. See below for the reasons:",
                fontBold: true,
                rowBreak: true,
                marginLeft: 24
            }));
            panel.add(new Label({
                text: message,
                marginLeft: 28,
                marginTop: 1,
                marginBottom: 1,
                padding: 0,
                rowBreak: true
            }));
            const pencil = new Image({
                name: ImageName.pencil,
                visible: true,
                height: 22,
                width: 22,
                marginLeft: 24,
                marginTop: 12,
                tooltip: "Edit load board posting",
                rowBreak: false
            });
            const cancel = new Image({
                name: ImageName.circleX,
                visible: true,
                height: 22,
                width: 22,
                marginTop: 12,
                tooltip: "Cancel load board posting",
                rowBreak: false
            });
            const refresh = new Image({
                name: ImageName.loadboardRefresh,
                visible: true,
                height: 22,
                width: 22,
                marginTop: 12,
                tooltip: "Refresh DAT load board posting",
                rowBreak: false
            });

            panel.add(pencil);
            if (!pencil.hasClickListeners()) {
                pencil.addClickListener(() => this.openLoadBoardSlide(tableRow));
            }

            panel.add(cancel);
            cancel.addClickListener(() => {
                this.changeLoadboardFlag(tableRow);
                imageLoadboardError.visible = false;
            });

            if (this.configureRefreshButton(refresh, tableRow, isDatRestControlActive, imageLoadboardError)) {
                panel.add(refresh);
            }

            if (!modelRow.isNull("pnn_rate")) {
                panel.add(new HorizontalSpacer({ id: "postedRateSpacer" }));
                panel.add(new Textbox({
                    id: "textboxPnnRate",
                    caption: "Posted Rate",
                    displayType: DisplayType.CURRENCY,
                    printable: true,
                    text: modelRow.getCurrency("pnn_rate").toString(true)
                }));
            }

            return panel;
        };
    }

    static hideImages(imageLoadboard, imageLoadboardError) {
        imageLoadboard.visible = false;
        imageLoadboardError.visible = false;
    }

    static displayDigitalOfferImage(tableRow: TableRow) {
        const imageDigitalOffer = tableRow.findComponentById("imageDigitalOffer") as Image;
        if (imageDigitalOffer == null) return;
        if (tableRow.data.getBoolean("digitally_booked")) {
            imageDigitalOffer.visible = true;
        } else {
            imageDigitalOffer.visible = false;
        }
    }

    static displayPackageErrorImage(tableRow: TableRow) {
        const imagePackageError = tableRow.findComponentById("imagePackageError") as Image;
        if (imagePackageError == null) return;
        if (tableRow.data.getBoolean("has_shipment_error")) {
            imagePackageError.visible = true;
            CommonTooltips.setTooltipContentLayout(imagePackageError, null, "lme/datafusion/StatusErrorQuickInfo", null, {
                "parent_id": tableRow.data.get("status_id"),
                "parent_tablename": "edistatus"
            })
            imagePackageError.tooltipDynamicOptions = { position: Alignment.BOTTOM };
        } else {
            imagePackageError.visible = false;
        }
    }

    static displayDetentionIcon(tableRow: TableRow) {
        const displayDetention = getDetentionControlBoolean("is_active", false);
        if (!displayDetention) return;
        const modelRow = tableRow.data as ModelRow;

        const imageOriginDetention = tableRow.findComponentById("imageOriginDetention") as Image;
        DetentionUtil.checkStopDetention(modelRow, "origin_detention", imageOriginDetention);

        const imageDestDetention = tableRow.findComponentById("imageDestDetention") as Image;
        DetentionUtil.checkStopDetention(modelRow, "destination_detention", imageDestDetention);

        if (!modelRow.isNull("stop_detention_statuses")) {
            const imageDetentionActive = tableRow.findComponentById("imageDetentionActive") as Image;
            const imageDetentionReview = tableRow.findComponentById("imageDetentionReview") as Image;
            const imageDetentionClosed = tableRow.findComponentById("imageDetentionClosed") as Image;
            DetentionUtil.checkMultipleStopsDetention(modelRow, "stop_detention_statuses", [
                imageDetentionActive, imageDetentionReview, imageDetentionClosed
            ]);
        }
    }

    static async setUpLoadBoardPostingSuccessfulTooltip(tableRow: TableRow, imageLoadboard: Image, imageLoadboardError: Image) {
        const result = await Model.searchSingleRecord("lme/powerbroker/pbw-loadboard-services", { service_type: "T" });
        const isDatRestControlActive = StringUtil.equalsIgnoreCase(result?.data?.is_active, "Y");

        imageLoadboard.visible = true;
        imageLoadboardError.visible = false;
        imageLoadboard.tooltipDynamicContent = () => {
            const modelRow = tableRow.data as ModelRow;
            const panel = new Panel({ padding: 0, margin: 0, wrap: true, maxWidth: 500 });
            panel.add(new Label({
                text: "Load Board Posting Successful",
                fontBold: true,
                color: "success",
                imageName: ImageName.check,
                rowBreak: true
            }));
            const pencil = new Image({
                name: ImageName.pencil,
                visible: true,
                height: 22,
                width: 22,
                marginLeft: 5,
                marginTop: 12,
                tooltip: "Edit load board posting",
                rowBreak: false
            });
            const cancel = new Image({
                name: ImageName.circleX,
                visible: true,
                height: 22,
                width: 22,
                marginTop: 12,
                tooltip: "Cancel load board posting",
                rowBreak: false
            });
            const refresh = new Image({
                name: ImageName.loadboardRefresh,
                height: 22,
                width: 22,
                marginTop: 12,
                tooltip: "Refresh DAT load board posting",
                rowBreak: false
            });

            panel.add(pencil);
            if (!pencil.hasClickListeners()) {
                pencil.addClickListener(() => this.openLoadBoardSlide(tableRow));
            }

            panel.add(cancel);
            cancel.addClickListener(() => {
                this.changeLoadboardFlag(tableRow);
                imageLoadboard.visible = false;
            });

            if (this.configureRefreshButton(refresh, tableRow, isDatRestControlActive, imageLoadboardError)) {
                panel.add(refresh);
            }

            if (!modelRow.isNull("pnn_rate")) {
                panel.add(new HorizontalSpacer({ id: "postedRateSpacer" }));
                panel.add(new Textbox({
                    id: "textboxPnnRate",
                    caption: "Posted Rate",
                    displayType: DisplayType.CURRENCY,
                    printable: true,
                    text: modelRow.getCurrency("pnn_rate").toString(true)
                }));
            }

            return panel;
        };
    }

    static refreshLoadPosting(tableRow: TableRow) {
        const data = tableRow.data;
        BrokerageMovementTableListeners.refreshLoadBoardPosting(data);
    }

    static async refreshLoadBoardPosting(data: ModelRow) {
        const orderId = data?.get("order_id");
        if (StringUtil.isEmptyString(orderId)) {
            return;
        }
        await Api.post("lme/general/pnn/dat-loadboard-refresh", {
            order_ids: [{ order_id: orderId }]
        }).then(response => {
            if (response.data?.[0]?.["some_loads_have_refresh_time_error"]) {
                Snackbar.showWarningSnackbar("The selected order was posted or refreshed within the last 15 minutes and is currently unavailable for refresh.");
            } else if (response.data?.[0]?.["dat_error"]) {
                if (response.data?.[0]?.["dat_error"] === "F") {
                    Snackbar.showWarningSnackbar("The selected order failed to refresh.");
                } else {
                    Snackbar.showWarningSnackbar("The selected order failed to refresh.\n\n" + response.data[0]["dat_error"]);
                }
            }
            if (response.data?.[0]?.["success_count"] == 1) {
                Toast.showSuccessToast("The refresh successfully updated to the DAT load board.");
            }

        });
    }

    static changeLoadboardFlag(tableRow: TableRow) {
        const data = tableRow.data;
        BrokerageMovementTableListeners.cancelLoadBoardPosting(data);
    }

    static async cancelLoadBoardPosting(data: ModelRow) {
        const orderId = data?.get("order_id");
        const movementId = data?.get("movement_id");
        if (StringUtil.isEmptyString(orderId) || StringUtil.isEmptyString(movementId)) {
            return;
        }
        Api.post("lme/powerbroker/cancel-loadboard-posting",
            { order_id: orderId, movement_id: movementId })
            .then(response => {
                if (response.data[0]?.is_cancel_successful) {
                    this.displaySuccessfulCancelToastMessage(orderId, movementId);
                    this.updateLoadboardFlag(orderId, true);
                } else {
                    this.displayFailedCancelToastMessage(orderId, response?.data[0]?.successful_vendor_cancellations, response?.data[0]?.failed_vendors);
                    this.updateLoadboardFlag(orderId, false);
                }
            })
    }

    static async updateLoadboardFlag(orderId: string, isSuccess: boolean) {
        const rowOrder = await new ModelOrders().searchSingle({ id: orderId });
        rowOrder.set("loadboard", false);
        rowOrder.set("loadboard_post_movement_id", "");
        rowOrder.set("loadboard_posting_cancelled", isSuccess);

        await rowOrder.post();
    }


    static displaySuccessfulCancelToastMessage(orderId: string, movementId: string) {
        const lbl = new Label({
            text: `Success!\n\rThe load posting for Order ${orderId} has been canceled`,
            align: HorizontalAlignment.CENTER,
            fontBold: true,
            fontSize: "xxlarge"
        });
        Toast.showToast(lbl, { backgroundColor: "primary" });
    }

    static displayFailedCancelToastMessage(orderId: string, successfulCancellationsVendors: string, failedCancellationsVendors: string) {
        if (!StringUtil.isEmptyString(successfulCancellationsVendors) && !StringUtil.isEmptyString(failedCancellationsVendors)) {
            const partialSuccessful = `Success! The load posting for ${orderId} has been canceled for: ${successfulCancellationsVendors}`;
            Toast.showToast(partialSuccessful, null, { persist: true });

            const partialFailure = `The load posting for Order ${orderId} was not canceled for: ${failedCancellationsVendors}.\r\nPlease retry directly on the load board site.`
            Snackbar.showWarningSnackbar(partialFailure);
        } else if (!StringUtil.isEmptyString(successfulCancellationsVendors)) {
            const loadBoardCancellationSuccessful = `${successfulCancellationsVendors} cancellation of load posting was successful.`;
            Toast.showToast(loadBoardCancellationSuccessful, null, { persist: true });
        } else if (!StringUtil.isEmptyString(failedCancellationsVendors)) {
            const loadBoardCancellationFailed = `${failedCancellationsVendors} cancellation of load posting was unsuccessful. Please retry directly on the load board site.`;
            Snackbar.showWarningSnackbar(loadBoardCancellationFailed);
        } else {
            // This condition occurs when there is not a matching load_board_trx record for the order_id and movement_id.
            const loadBoardCancellationFailed = `Cancellation of load posting was unsuccessful. Please retry directly on the load board site.`;
            Snackbar.showWarningSnackbar(loadBoardCancellationFailed);
        }
    }

    static openLoadBoardSlide(tableRow: TableRow): void {
        const data = tableRow.data;
        const exceptionReasons = data?.get("loadboard_exception_list");
        const pbwLoadboardServices = data?.get("load_board_vendor_list");
        const orderId = data?.get("order_id");
        const origStopType = data?.get("origin_stop_type");
        const destStopType = data?.get("destination_stop_type");
        const movementId = data?.get("posted_by_movement") || (!data?.get("loadboard") && this.hasValidStopType(origStopType, destStopType)) ? data?.get("movement_id") : null;
        const originStopId = movementId === null ? data.get("order_origin_stop_id") : data.get("origin_stop_id");


        const origCity = data?.get("origin_city");
        const origState = data?.get("origin_state");
        const destCity = data?.get("destination_city");
        const destState = data?.get("destination_state");

        let postTitle = "Post to Load Boards - Order " + orderId;

        if (movementId !== null) {
            postTitle = postTitle + '\t' + StringUtil.toProperCase(origCity) + ', ' + origState + ' to ' + StringUtil.toProperCase(destCity) + ', ' + destState;
        }
        PostToLoadBoards.showInSlideoutDecorator(orderId, postTitle, originStopId, movementId, exceptionReasons, false, pbwLoadboardServices);
    }

    static hasValidStopType(origStopType: string, destStopType: string) {
        return ('SP' === origStopType || 'SD' === origStopType) || ('SP' === destStopType || 'SD' === destStopType);
    }


    static openStatusSlideoutBPListener(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        const id = row.data.get("order_id");
        const moveId = row.data.get("movement_id");
        const customerId = row.data.get("customer_id");
        if (id) {
            const layout = Layout.getLayout("lme/dispatch/ShipmentStatusSlideout", {
                width: window.innerWidth * .70,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as ShipmentStatusSlideout;
            new SlideoutDecorator({
                layout: layout,
                title: "DataFusion - Order " + id,
                fillVerticalSpace: true,
                addlComponents: [layout.saveButton],
                doAfterSlideIn: (decorator: SlideoutDecorator) => {
                    layout.sourceEdistatus.search({ order_id: id });
                    layout.sourceEdiOrder.search({ order_id: id, movement_id: moveId });
                    layout.sourceEdiMovementCarrier.search({ movement_id: moveId });
                    layout.tabsetDataFusion.selectedIndex = layout.tabsetDataFusion.indexOf(layout.tabOutbound);
                    layout.saveButton.addClickListener(async event => {
                        if (decorator.validateSimple()) {
                            await layout.saveChanges();
                        }
                    });
                }
            });
            layout.orderId = id;
            layout.movementId = moveId;
            layout.customerId = customerId;
        }
    }

    static openICSlideoutBPListener(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        const id = row.data.get("order_id");
        const moveId = row.data.get("movement_id");
        const customerId = row.data.get("customer_id");
        const props = {
            orderId: id,
            moveId: moveId,
            customerId: customerId,
        };
        PlanningUtil.openICSlideout(props);
    }

    static panelBrkStatusOnClickBPListener(event: ClickEvent, layoutBrokeragePlanningToolPanel: BrokeragePlanningToolPanel, tableMovements: Table) {
        const panel = event.target as Panel;
        if (panel["statusDropDownItems"] != null)
            displayBrkStatusDropDowns(panel);
        else {
            const row = TableRow.getContainingTableRow(panel);
            const buttonDispatch: Button = layoutBrokeragePlanningToolPanel.findComponentById("buttonDispatch") as Button;
            populateAndDisplayBrkStatusDropDowns(panel, row?.data?.get("movement_id"), tableMovements, buttonDispatch);
        }
    }

    static imageAppointmentOnClickBPListener(event: ClickEvent) {
        this.showMultiStopSlideout(event);
    }

    static async showMultiStopSlideout(event) {
        const label = event.target as Label;
        label.busy = true;
        const tableRow: TableRow = TableRow.getContainingTableRow(event.target);
        tableRow.table.selectedRow = tableRow;
        const row = tableRow.data;
        const isManifest = row.get("manifest_id") != null;
        const detentionClick = event.target.id.toLowerCase().includes("detention");
        const displayed = await MultiStop.showSlideout({ isManifest, row, isDetention: detentionClick, doAfterSlideOut: () => label.busy = false });
        if (displayed === false) {
            label.busy = false;
        }
    }

    static onRowExpandBPListener(event: TableRowExpansionEvent, tableMovements: Table, brokeragePlanning: BrokeragePlanning, type: BrokerageMovementTableType) {
        tableMovements.expandComponent = new Panel();
        if ((event.target as TableRow).data != null) {
            const row = (event.target as TableRow).data;
            const expansion = Layout.getLayout("lme/powerbroker/BrokerageMovementsGridExpansion") as BrokerageMovementsGridExpansion;
            expansion.addLayoutLoadListener(() => {
                expansion.id = "ExpandedBrokerageMovementsGrid";
                expansion.initialize(
                    event.target,
                    row.get("movement_id"),
                    row.get("order_id"),
                    row.get("move_status"),
                    brokeragePlanning.mainDataSource,
                    type
                );
                (event.expandComponentParent as Panel).add(expansion);
            });
        }
    }

    static orderNumberOnClickBPListener(event: ClickEvent) {
        OrderTabView.navigateTo({ key: (event.target as Label).text, newTab: true });
    }

    static customerIdOnClickBPListener(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        Customer.navigateTo(row.data.get("customer_id"));
    }

    static manifestIdOnClickBPListener(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        Navigation.navigateTo("lme/dispatch/OrderList?manifest_id=" + row.data.get("manifest_id"), { newTab: true });
    }

    static badgeBrTrackStatusOnDataDisplayBPListener(event: DataDisplayEvent) {
        const badgeBrTrackStatus = event.target as Label;
        const brTrackStatusForBadge = event.rowData?.get("br_track_status_for_badge");
        badgeBrTrackStatus.text = brTrackStatusForBadge || "--";
        badgeBrTrackStatus.backgroundColor = event.rowData?.get("br_track_status_color");
        badgeBrTrackStatus.tooltipDynamicContent = () => new BrTrackingQuickInfoPanel(event.rowData);
    }

    static badgeBrTrackStatusOnDblClickBPListener(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        const brDetailsLink: string = row.data.get("br_details_link");
        if (!StringUtil.isEmptyString(brDetailsLink)) {
            window.open(brDetailsLink, "_blank");
        }
    }

    static imageLoadboardOnClickBPListener(event: ClickEvent) {
        const clickedRow = TableRow.getContainingTableRow(event.target as Component);
        const orderId = clickedRow.data.get("order_id");
        this.cancelLoadBoardPosting(clickedRow.data);
    }

    static sendSmsBtnOnClickBPListener(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        const modelRow = row?.data;
        SmsUtility.showSendSmsMessageSlideout(SmsUtility.setLayoutProps(modelRow, false, false));
    }

    static cellStopsOnClickBPListener(event: ClickEvent) {
        this.showMultiStopSlideout(event);
    }

    static configureRefreshButton(refresh: Image, tableRow: TableRow, isDatRestControlActive: boolean, imageToHide: Image) {
        if (tableRow.data.get("loadboard_last_refresh") && isDatRestControlActive) {
            const lastRefreshDate = new Date(tableRow.data.get("loadboard_last_refresh"));
            const fifteenMinutesAgo = new Date(Date.now() - 15 * 60 * 1000);
            refresh.visible = true;

            if (lastRefreshDate < fifteenMinutesAgo) {
                refresh.enabled = true;
                refresh.color = "default";
                refresh.tooltip = "Refresh DAT load board posting";
                refresh.addClickListener(() => {
                    this.refreshLoadPosting(tableRow);
                    imageToHide.visible = false;
                });
            } else {
                refresh.enabled = false;
                refresh.color = "subtle";
                refresh.tooltip = "The load refreshed within the last 15 minutes";
            }
            return true;
        }
        refresh.visible = false;
        return false;
    }
}

export enum MovementStatus {
    AVAILABLE = "A",
    COVERED = "C",
    PROGRESS = "P",
    DELIVERED = "D"
}
